export type MeetingMinuteDepartment =
  | 'QUALITY'
  | 'COMMERCIAL'
  | 'FINANCE'
  | 'HR'
  | 'IT'
  | 'MANAGEMENT'
  | 'TECHNICAL';

export type MeetingMinuteParticipantStatus = 'PARTICIPANT' | 'MISSING';

export interface MeetingMinuteUser {
  userId: number;
  firstName: string;
  lastName: string;
  [key: string]: any;
}

export interface MeetingMinuteParticipant {
  meetingMinuteParticipantId: number | null;
  user: MeetingMinuteUser | null;
  status: MeetingMinuteParticipantStatus | null;
  createTime?: string;
  updateTime?: string;
}

export interface MeetingMinuteAction {
  meetingMinuteActionId: number | null;
  task: string;
  responsibleUser: MeetingMinuteUser | null;
  date: string | null;
  comment: string;
  createTime?: string;
  updateTime?: string;
}

export interface MeetingMinuteAgreement {
  meetingMinuteAgreementId: number | null;
  date: string | null;
  comment: string;
  createTime?: string;
  updateTime?: string;
}

export interface MeetingMinuteRisk {
  meetingMinuteRiskId: number | null;
  riskDetected: string;
  description: string;
  reportedByUser: MeetingMinuteUser | null;
  reportedDate: string | null;
  assignToUser: MeetingMinuteUser | null;
  action: string | null;
  createTime?: string;
  updateTime?: string;
}

export interface MeetingMinuteNext {
  meetingMinuteNextId: number | null;
  topics: string;
  date: string | null;
  place: string;
  createTime?: string;
  updateTime?: string;
}

export interface MeetingMinute {
  meetingMinuteId: number | null;
  subject: string;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  place: string;
  writtenByUser: MeetingMinuteUser | null;
  reviewedByUser: MeetingMinuteUser | null;
  proposedTopic: string;
  developmentOfTopic: string;
  department: MeetingMinuteDepartment | null;
  reminder: string | null;
  participants: MeetingMinuteParticipant[];
  actions: MeetingMinuteAction[];
  agreements: MeetingMinuteAgreement[];
  risks: MeetingMinuteRisk[];
  nextMeetings: MeetingMinuteNext[];
  createTime?: string;
  updateTime?: string;
}

export interface MeetingMinuteParticipantPayload {
  meetingMinuteParticipantId: number | null;
  userId: number;
  status: MeetingMinuteParticipantStatus;
}

export interface MeetingMinuteActionPayload {
  meetingMinuteActionId: number | null;
  task: string;
  responsibleUserId: number;
  date: string | null;
  comment: string;
}

export interface MeetingMinuteAgreementPayload {
  meetingMinuteAgreementId: number | null;
  date: string | null;
  comment: string;
}

export interface MeetingMinuteRiskPayload {
  meetingMinuteRiskId: number | null;
  riskDetected: string;
  description: string;
  reportedByUserId: number;
  reportedDate: string | null;
  assignToUserId: number;
  action: string | null;
}

export interface MeetingMinuteNextPayload {
  meetingMinuteNextId: number | null;
  topics: string;
  date: string | null;
  place: string;
}

export interface MeetingMinutePayload {
  subject: string;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  place: string;
  writtenByUserId: number;
  reviewedByUserId: number;
  proposedTopic: string;
  developmentOfTopic: string;
  department: MeetingMinuteDepartment;
  reminder: string | null;
  participants: MeetingMinuteParticipantPayload[];
  actions: MeetingMinuteActionPayload[];
  agreements: MeetingMinuteAgreementPayload[];
  risks: MeetingMinuteRiskPayload[];
  nextMeetings: MeetingMinuteNextPayload[];
}

export const MEETING_MINUTE_DEPARTMENT_LABELS: Record<MeetingMinuteDepartment, string> = {
  QUALITY: 'Quality',
  COMMERCIAL: 'Commercial',
  FINANCE: 'Finance',
  HR: 'HR',
  IT: 'IT',
  MANAGEMENT: 'Management',
  TECHNICAL: 'Technical'
};

export const MEETING_MINUTE_PARTICIPANT_STATUS_LABELS: Record<MeetingMinuteParticipantStatus, string> = {
  PARTICIPANT: 'Participant',
  MISSING: 'Missing'
};

export const MEETING_MINUTE_ERROR_MESSAGES: Record<string, string> = {
  MEETING_MINUTE_NOT_FOUND: 'Meeting minute not found.',
  MEETING_MINUTE_PARTICIPANT_NOT_FOUND: 'Meeting minute participant not found.',
  MEETING_MINUTE_ACTION_NOT_FOUND: 'Meeting minute action not found.',
  MEETING_MINUTE_AGREEMENT_NOT_FOUND: 'Meeting minute agreement not found.',
  MEETING_MINUTE_RISK_NOT_FOUND: 'Meeting minute risk not found.',
  MEETING_MINUTE_NEXT_NOT_FOUND: 'Next meeting not found.'
};

export function meetingMinuteUserFullName(user?: MeetingMinuteUser | null): string {
  if (!user) {
    return '';
  }
  return `${user.lastName ?? ''} ${user.firstName ?? ''}`.trim();
}

export function meetingMinuteDepartmentLabel(department?: MeetingMinuteDepartment | null): string {
  return department ? MEETING_MINUTE_DEPARTMENT_LABELS[department] ?? department : '';
}

export function meetingMinuteParticipantStatusLabel(status?: MeetingMinuteParticipantStatus | null): string {
  return status ? MEETING_MINUTE_PARTICIPANT_STATUS_LABELS[status] ?? status : '';
}
